import React, { useState, useEffect, useMemo } from 'react';
import { auth, db } from "./firebase";
import { signInWithPopup, GoogleAuthProvider, onAuthStateChanged, signOut } from "firebase/auth";
import { collection, query, orderBy, onSnapshot, addDoc, doc, updateDoc, deleteDoc, getDoc } from "firebase/firestore";

// --- Utility Functions ---

const formatDate = (timestamp) => {
  if (!timestamp?.toDate) return "Unknown";
  const date = timestamp.toDate();
  return date.toLocaleDateString("en-US", { 
    weekday: "long",
    month: "short", 
    day: "numeric",
    year: "numeric"
  });
};

const formatTime = (timestamp) => {
  if (!timestamp?.toDate) return "";
  const date = timestamp.toDate();
  return date.toLocaleTimeString("en-US", { 
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short"
  });
};

const formatDuration = (secs) => {
  if (!secs) return "";
  const mins = Math.floor(secs / 60);
  const seconds = secs % 60;
  return `${mins}:${seconds.toString().padStart(2, '0')}`;
};

const formatDurationLong = (secs) => {
  if (!secs) return "0:00";
  const mins = Math.floor(secs / 60);
  const seconds = secs % 60;
  return `${mins}:${seconds.toString().padStart(2, '0')}`;
};

const getInitials = (name) => {
  if (!name) return "??";
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const isToday = (timestamp) => {
  if (!timestamp?.toDate) return false;
  const date = timestamp.toDate();
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

const isSameDate = (timestamp, targetDate) => {
  if (!timestamp?.toDate) return false;
  const date = timestamp.toDate();
  return date.toDateString() === targetDate.toDateString();
};

const isInDateRange = (timestamp, startDate, endDate) => {
  if (!timestamp?.toDate) return false;
  const date = timestamp.toDate();
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);
  return date >= start && date <= end;
};

const getDateRange = (viewMode, selectedDate) => {
  const date = new Date(selectedDate);
  
  if (viewMode === 'daily') {
    return { startDate: date, endDate: date };
  }
  
  if (viewMode === 'weekly') {
    const start = new Date(date);
    start.setDate(date.getDate() - date.getDay());
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { startDate: start, endDate: end };
  }
  
  if (viewMode === 'monthly') {
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return { startDate: start, endDate: end };
  }
  
  return { startDate: date, endDate: date };
};

const formatDateRange = (viewMode, startDate, endDate) => {
  if (viewMode === 'daily') {
    return startDate.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  }
  
  if (viewMode === 'weekly') {
    const startStr = startDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const endStr = endDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return `${startStr} - ${endStr}`;
  }
  
  if (viewMode === 'monthly') {
    return startDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  }
  
  return '';
};

const isThisWeek = (timestamp) => {
  if (!timestamp?.toDate) return false;
  const date = timestamp.toDate();
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  return date >= weekAgo;
};

const normalizePhone = (phone) => {
  if (!phone) return '';
  return String(phone).replace(/\D/g, '').slice(-10);
};

const getCallPhoneNumber = (call) => {
  return call?.rawPayload?.data?.user_id ||
         call?.rawPayload?.user_id ||
         call?.rawPayload?.to ||
         call?.rawPayload?.call?.to ||
         call?.rawPayload?.metadata?.to_number ||
         call?.rawPayload?.metadata?.to ||
         call?.rawPayload?.data?.to ||
         call?.rawPayload?.phone_number ||
         call?.rawPayload?.metadata?.caller_id ||
         call?.rawPayload?.data?.conversation_initiation_client_data?.dynamic_variables?.system__caller_id ||
         call?.rawPayload?.data?.conversation_initiation_client_data?.dynamic_variables?.system__called_number ||
         null;
};

// ============================================================
// BRAND TOKENS (from Remi Brand Brief v2.0)
// ============================================================
// Background:    #FAF7F2 (warm white)
// Card/Surface:  #F5EDE3 (warm cream) or white
// Text Primary:  #3D2E1E (deep walnut)
// Text Secondary: #2C2825 at 55% opacity
// Text Muted:    #2C2825 at 35% opacity
// Accent:        #C4935A (amber glow)
// Terracotta:    #B87A5E (secondary accent)
// Sage:          #8A9A7B (success/positive)
// Dusty Rose:    #C4A49A (soft highlight)
// Border:        rgba(61,46,30,0.08)
// Headings:      'Lora', serif
// Body:          'DM Sans', sans-serif
// ============================================================

// --- Status Badge Component ---

const StatusBadge = ({ status }) => {
  const styles = {
    completed: "bg-[#8A9A7B]/10 text-[#8A9A7B]",
    scheduled: "bg-[#C4935A]/10 text-[#C4935A]",
    failed: "bg-red-500/10 text-red-500",
    "no-answer": "bg-[#B87A5E]/10 text-[#B87A5E]",
    active: "bg-[#8A9A7B]/10 text-[#8A9A7B]",
    missed: "bg-[#B87A5E]/10 text-[#B87A5E]",
  };

  const labels = {
    completed: "Completed",
    scheduled: "Scheduled",
    failed: "Failed",
    "no-answer": "No Answer",
    active: "Active",
    missed: "Missed",
  };

  const isPulsing = status === "scheduled";

  return (
    <span className={`text-xs font-semibold px-3 py-1 rounded-full inline-flex items-center gap-1.5 ${styles[status] || styles.completed}`}>
      <span className={`w-1.5 h-1.5 rounded-full bg-current ${isPulsing ? 'animate-pulse-dot' : ''}`} />
      {labels[status] || status}
    </span>
  );
};

// --- Add/Edit Family Modal Component ---

const getTodayDateString = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

const DEFAULT_RECURRING_SCHEDULE = { type: 'recurring', day: 'Monday', time: '10:00 AM' };
const DEFAULT_ONETIME_SCHEDULE = { type: 'one-time', date: getTodayDateString(), time: '10:00 AM' };

const DEFAULT_FORM_DATA = {
  name: '',
  nickname: '',
  relationship: '',
  phone: '',
  familyContext: '',
  callSchedule: [{ ...DEFAULT_RECURRING_SCHEDULE }],
  planType: 'essential',
  timezone: 'America/New_York',
};

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TIMES = [
  '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
  '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM',
  '6:00 PM', '7:00 PM', '8:00 PM'
];
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
];
const PLAN_TYPES = [
  { value: 'essential', label: 'Essential ($59/mo)', price: 59 },
  { value: 'legacy', label: 'Legacy ($99/mo)', price: 99 },
  { value: 'test', label: 'Test ($0)', price: 0 },
  { value: 'inactive', label: 'Inactive ($0)', price: 0 },
];
const RELATIONSHIPS = [
  { value: '', label: 'Select relationship' },
  { value: 'mom', label: 'Mom' },
  { value: 'dad', label: 'Dad' },
  { value: 'grandma', label: 'Grandma' },
  { value: 'grandpa', label: 'Grandpa' },
  { value: 'aunt', label: 'Aunt' },
  { value: 'uncle', label: 'Uncle' },
  { value: 'other', label: 'Other' },
];
const COST_RATES = {
  ELEVENLABS_BASE_MONTHLY: 22,
  ELEVENLABS_INCLUDED_MINUTES: 250,
  ELEVENLABS_OVERAGE_PER_MIN: 0.12,
  TWILIO_PER_MIN: 0.009,
  CLAUDE_PER_CALL: 0.015,
  PLAN_PRICES: { essential: 59, legacy: 99, test: 0, inactive: 0 },
};

const FamilyModal = ({ isOpen, onClose, onSave, onDelete, editFamily }) => {
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isEditMode = !!editFamily;

  useEffect(() => {
    if (isOpen) {
      if (editFamily) {
        let callSchedule = [];
        
        if (editFamily.callSchedule && Array.isArray(editFamily.callSchedule) && editFamily.callSchedule.length > 0) {
          if (editFamily.callSchedule[0].type) {
            callSchedule = editFamily.callSchedule;
          } else {
            callSchedule = editFamily.callSchedule.map(s => ({
              type: 'recurring',
              day: s.day,
              time: s.time,
            }));
          }
        }
        else if (editFamily.recurringSchedule || editFamily.oneOffCalls) {
          if (editFamily.recurringSchedule && Array.isArray(editFamily.recurringSchedule)) {
            callSchedule.push(...editFamily.recurringSchedule.map(s => ({
              type: 'recurring',
              day: s.day,
              time: s.time,
            })));
          }
          if (editFamily.oneOffCalls && Array.isArray(editFamily.oneOffCalls)) {
            callSchedule.push(...editFamily.oneOffCalls.map(s => ({
              type: 'one-time',
              date: s.date,
              time: s.time,
            })));
          }
        }
        else if (editFamily.callDay && editFamily.callTime) {
          callSchedule = [{ type: 'recurring', day: editFamily.callDay, time: editFamily.callTime }];
        }
        
        if (callSchedule.length === 0) {
          callSchedule = [{ ...DEFAULT_RECURRING_SCHEDULE }];
        }
        
        setFormData({
          name: editFamily.name || '',
          nickname: editFamily.nickname || '',
          relationship: editFamily.relationship || '',
          phone: editFamily.phone || '',
          familyContext: editFamily.familyContext || '',
          callSchedule: callSchedule,
          planType: editFamily.planType || 'essential',
          timezone: editFamily.timezone || 'America/New_York',
        });
      } else {
        setFormData({ 
          ...DEFAULT_FORM_DATA, 
          callSchedule: [{ ...DEFAULT_RECURRING_SCHEDULE }],
        });
      }
    }
  }, [editFamily, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleScheduleChange = (index, field, value) => {
    setFormData(prev => {
      const newSchedule = [...prev.callSchedule];
      
      if (field === 'type') {
        if (value === 'recurring') {
          newSchedule[index] = { type: 'recurring', day: 'Monday', time: newSchedule[index].time || '10:00 AM' };
        } else {
          newSchedule[index] = { type: 'one-time', date: getTodayDateString(), time: newSchedule[index].time || '10:00 AM' };
        }
      } else {
        newSchedule[index] = { ...newSchedule[index], [field]: value };
      }
      
      return { ...prev, callSchedule: newSchedule };
    });
  };

  const addSchedule = () => {
    setFormData(prev => ({
      ...prev,
      callSchedule: [...prev.callSchedule, { ...DEFAULT_RECURRING_SCHEDULE }]
    }));
  };

  const removeSchedule = (index) => {
    if (formData.callSchedule.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      callSchedule: prev.callSchedule.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const dataToSave = {
        name: formData.name,
        nickname: formData.nickname,
        relationship: formData.relationship,
        phone: formData.phone,
        familyContext: formData.familyContext,
        callSchedule: formData.callSchedule.map(s => ({...s, completed: false})),
        planType: formData.planType,
        timezone: formData.timezone,
      };
      console.log('[FamilyModal handleSubmit] dataToSave:', dataToSave);
      console.log('[FamilyModal handleSubmit] dataToSave.callSchedule:', JSON.stringify(dataToSave.callSchedule, null, 2));
      
      if (isEditMode) {
        console.log('[FamilyModal handleSubmit] Edit mode - calling onSave with id:', editFamily.id);
        await onSave(editFamily.id, dataToSave);
      } else {
        const familyData = {
          ...dataToSave,
          agentID: 'agent_7701kgfwy1safbftk526gca0j32t',
          phoneNumberId: 'phnum_1701k81je5xte6pa99w5h0taac5q',
          createdAt: new Date(),
          callCount: 0,
          lastConversationSummary: '',
        };
        console.log('[FamilyModal handleSubmit] New mode - calling onSave');
        await onSave(null, familyData);
      }
      console.log('[FamilyModal handleSubmit] onSave completed successfully');
      setFormData({ 
        ...DEFAULT_FORM_DATA, 
        callSchedule: [{ ...DEFAULT_RECURRING_SCHEDULE }],
      });
      onClose();
    } catch (error) {
      console.error('[FamilyModal handleSubmit] Error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to remove this family?')) {
      return;
    }
    
    setDeleting(true);
    try {
      await onDelete(editFamily.id);
      setFormData({ 
        ...DEFAULT_FORM_DATA, 
        callSchedule: [{ ...DEFAULT_RECURRING_SCHEDULE }],
      });
      onClose();
    } catch (error) {
      console.error('Error deleting family:', error);
    } finally {
      setDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-[#3D2E1E]/40 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white border border-[rgba(61,46,30,0.1)] rounded-2xl w-full max-w-xl mx-4 shadow-2xl animate-fade-in max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-5 border-b border-[rgba(61,46,30,0.08)]">
          <h2 className="font-['Lora'] text-xl font-medium text-[#3D2E1E]">
            {isEditMode ? 'Edit Family' : 'Add New Family'}
          </h2>
          <button 
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-[#F5EDE3] flex items-center justify-center hover:bg-[#EDE4D8] transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3D2E1E" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Name */}
          <div>
            <label className="block text-[10px] uppercase tracking-[2px] text-[#C4935A] font-medium mb-2">Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full bg-[#FAF7F2] border border-[rgba(61,46,30,0.1)] rounded-lg px-4 py-3 text-[#3D2E1E] placeholder-[#2C2825]/30 focus:outline-none focus:border-[#C4935A] transition-colors"
              placeholder="Full name"
            />
          </div>

          {/* Nickname */}
          <div>
            <label className="block text-[10px] uppercase tracking-[2px] text-[#C4935A] font-medium mb-2">Nickname</label>
            <input
              type="text"
              name="nickname"
              value={formData.nickname}
              onChange={handleChange}
              className="w-full bg-[#FAF7F2] border border-[rgba(61,46,30,0.1)] rounded-lg px-4 py-3 text-[#3D2E1E] placeholder-[#2C2825]/30 focus:outline-none focus:border-[#C4935A] transition-colors"
              placeholder="What Remi should call them"
            />
          </div>

          {/* Relationship */}
          <div>
            <label className="block text-[10px] uppercase tracking-[2px] text-[#C4935A] font-medium mb-2">Relationship to Customer</label>
            <select
              name="relationship"
              value={formData.relationship}
              onChange={handleChange}
              className="w-full bg-[#FAF7F2] border border-[rgba(61,46,30,0.1)] rounded-lg px-4 py-3 text-[#3D2E1E] focus:outline-none focus:border-[#C4935A] transition-colors appearance-none cursor-pointer"
            >
              {RELATIONSHIPS.map(rel => (
                <option key={rel.value} value={rel.value}>{rel.label}</option>
              ))}
            </select>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-[10px] uppercase tracking-[2px] text-[#C4935A] font-medium mb-2">Phone Number</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="w-full bg-[#FAF7F2] border border-[rgba(61,46,30,0.1)] rounded-lg px-4 py-3 text-[#3D2E1E] placeholder-[#2C2825]/30 focus:outline-none focus:border-[#C4935A] transition-colors"
              placeholder="+1 (555) 123-4567"
            />
          </div>

          {/* Family Context */}
          <div>
            <label className="block text-[10px] uppercase tracking-[2px] text-[#C4935A] font-medium mb-2">Family Context</label>
            <textarea
              name="familyContext"
              value={formData.familyContext}
              onChange={handleChange}
              rows={3}
              className="w-full bg-[#FAF7F2] border border-[rgba(61,46,30,0.1)] rounded-lg px-4 py-3 text-[#3D2E1E] placeholder-[#2C2825]/30 focus:outline-none focus:border-[#C4935A] transition-colors resize-none"
              placeholder="Background info Remi should know about this person..."
            />
          </div>

          {/* Call Schedule */}
          <div>
            <label className="block text-[10px] uppercase tracking-[2px] text-[#C4935A] font-medium mb-2">Call Schedule</label>
            <div className="space-y-2">
              {formData.callSchedule.map((schedule, index) => (
                <div key={index} className="flex gap-2 items-center bg-[#FAF7F2] border border-[rgba(61,46,30,0.1)] rounded-lg p-2">
                  {/* Type Toggle */}
                  <select
                    value={schedule.type}
                    onChange={(e) => handleScheduleChange(index, 'type', e.target.value)}
                    className="bg-white border border-[rgba(61,46,30,0.1)] rounded-lg px-2 py-2 text-[#C4935A] text-xs font-medium focus:outline-none focus:border-[#C4935A] transition-colors appearance-none cursor-pointer"
                  >
                    <option value="recurring">Recurring</option>
                    <option value="one-time">One-Time</option>
                  </select>
                  
                  {schedule.type === 'recurring' ? (
                    <select
                      value={schedule.day || 'Monday'}
                      onChange={(e) => handleScheduleChange(index, 'day', e.target.value)}
                      className="flex-1 bg-white border border-[rgba(61,46,30,0.1)] rounded-lg px-3 py-2 text-[#3D2E1E] text-sm focus:outline-none focus:border-[#C4935A] transition-colors appearance-none cursor-pointer"
                    >
                      {DAYS.map(day => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="date"
                      value={schedule.date || getTodayDateString()}
                      onChange={(e) => handleScheduleChange(index, 'date', e.target.value)}
                      className="flex-1 bg-white border border-[rgba(61,46,30,0.1)] rounded-lg px-3 py-2 text-[#3D2E1E] text-sm focus:outline-none focus:border-[#C4935A] transition-colors"
                    />
                  )}
                  
                  <input
                    type="time"
                    value={schedule.time ? (
                      (() => {
                        if (/^\d{2}:\d{2}$/.test(schedule.time)) return schedule.time;
                        const [time, modifier] = schedule.time.split(" ");
                        let [hours, minutes] = time.split(':');
                        if (modifier === "PM" && hours !== "12") {
                          hours = String(Number(hours) + 12);
                        } else if (modifier === "AM" && hours === "12") {
                          hours = "00";
                        }
                        return `${hours.padStart(2, '0')}:${minutes}`;
                      })()
                    ) : ""}
                    onChange={(e) => handleScheduleChange(index, 'time', e.target.value)}
                    className="w-32 bg-white border border-[rgba(61,46,30,0.1)] rounded-lg px-2 py-2 text-[#3D2E1E] text-sm focus:outline-none focus:border-[#C4935A] transition-colors appearance-none cursor-pointer"
                  />
                  
                  {formData.callSchedule.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSchedule(index)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors flex-shrink-0"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  )}
                </div>
              ))}
              
              <button
                type="button"
                onClick={addSchedule}
                className="w-full py-2 border border-dashed border-[rgba(61,46,30,0.15)] rounded-lg text-[#C4935A] text-sm font-medium hover:border-[#C4935A] hover:bg-[#C4935A]/5 transition-colors"
              >
                + Add Call
              </button>
            </div>
          </div>

          {/* Plan Type */}
          <div>
            <label className="block text-[10px] uppercase tracking-[2px] text-[#C4935A] font-medium mb-2">Plan Type</label>
            <select
              name="planType"
              value={formData.planType}
              onChange={handleChange}
              className="w-full bg-[#FAF7F2] border border-[rgba(61,46,30,0.1)] rounded-lg px-4 py-3 text-[#3D2E1E] focus:outline-none focus:border-[#C4935A] transition-colors appearance-none cursor-pointer"
            >
              {PLAN_TYPES.map(plan => (
                <option key={plan.value} value={plan.value}>{plan.label}</option>
              ))}
            </select>
          </div>

          {/* Timezone */}
          <div>
            <label className="block text-[10px] uppercase tracking-[2px] text-[#C4935A] font-medium mb-2">Time Zone</label>
            <select
              name="timezone"
              value={formData.timezone}
              onChange={handleChange}
              className="w-full bg-[#FAF7F2] border border-[rgba(61,46,30,0.1)] rounded-lg px-4 py-3 text-[#3D2E1E] focus:outline-none focus:border-[#C4935A] transition-colors appearance-none cursor-pointer"
            >
              {TIMEZONES.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            {isEditMode && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-50 border border-red-200 text-red-500 px-5 py-3 rounded-lg font-semibold text-sm hover:bg-red-100 hover:border-red-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-transparent border border-[rgba(61,46,30,0.12)] text-[#2C2825]/55 px-5 py-3 rounded-lg font-semibold text-sm hover:border-[rgba(61,46,30,0.25)] hover:text-[#3D2E1E] transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-[#C4935A] text-white px-5 py-3 rounded-lg font-semibold text-sm hover:bg-[#B8874E] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Save Family')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Header Component ---

const Header = ({ onAddFamily, selectedDate, onDateChange, viewMode, onViewModeChange }) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  const { startDate, endDate } = getDateRange(viewMode, selectedDate);
  const formattedDate = formatDateRange(viewMode, startDate, endDate);

  const today = new Date();
  const isCurrentPeriod = viewMode === 'daily' 
    ? selectedDate.toDateString() === today.toDateString()
    : viewMode === 'weekly'
      ? isInDateRange({ toDate: () => today }, startDate, endDate)
      : selectedDate.getMonth() === today.getMonth() && selectedDate.getFullYear() === today.getFullYear();

  const handleDateChange = (e) => {
    const newDate = new Date(e.target.value + 'T12:00:00');
    onDateChange(newDate);
    setShowDatePicker(false);
  };

  const handleMonthChange = (e) => {
    const [year, month] = e.target.value.split('-');
    const newDate = new Date(parseInt(year), parseInt(month) - 1, 1);
    onDateChange(newDate);
    setShowDatePicker(false);
  };

  const resetToToday = () => {
    onDateChange(new Date());
    setShowDatePicker(false);
  };

  const navigatePeriod = (direction) => {
    const newDate = new Date(selectedDate);
    if (viewMode === 'daily') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (viewMode === 'weekly') {
      newDate.setDate(newDate.getDate() + (7 * direction));
    } else if (viewMode === 'monthly') {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    onDateChange(newDate);
  };

  const inputDateValue = selectedDate.toISOString().split('T')[0];
  const inputMonthValue = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}`;

  return (
    <div className="flex justify-between items-center mb-8 pb-6 border-b border-[rgba(61,46,30,0.08)]">
      <h1 className="font-['Lora'] text-[28px] font-medium tracking-tight text-[#3D2E1E]">
        <span className="text-[#C4935A]">Sonny</span> Admin
      </h1>
      <div className="flex items-center gap-3">
        {/* View Mode Toggle */}
        <div className="flex bg-[#F5EDE3] rounded-lg p-0.5 border border-[rgba(61,46,30,0.06)]">
          {['daily', 'weekly', 'monthly'].map((mode) => (
            <button
              key={mode}
              onClick={() => onViewModeChange(mode)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all capitalize ${
                viewMode === mode
                  ? 'bg-[#C4935A] text-white shadow-sm'
                  : 'text-[#2C2825]/55 hover:text-[#3D2E1E]'
              }`}
            >
              {mode}
            </button>
          ))}
        </div>

        {/* Date Navigation */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => navigatePeriod(-1)}
            className="p-1.5 text-[#2C2825]/45 hover:text-[#3D2E1E] hover:bg-[#F5EDE3] rounded transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <div className="relative">
            <button 
              onClick={() => setShowDatePicker(!showDatePicker)}
              className={`text-sm px-3 py-1.5 rounded-lg transition-all flex items-center gap-2 min-w-[180px] justify-center ${
                isCurrentPeriod 
                  ? 'text-[#2C2825]/55 hover:text-[#3D2E1E] hover:bg-[#F5EDE3]' 
                  : 'text-[#C4935A] bg-[#C4935A]/8 hover:bg-[#C4935A]/15'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {formattedDate}
            </button>
            
            {showDatePicker && (
              <div className="absolute right-0 top-full mt-2 bg-white border border-[rgba(61,46,30,0.1)] rounded-lg p-3 shadow-xl z-50">
                {viewMode === 'monthly' ? (
                  <input
                    type="month"
                    value={inputMonthValue}
                    onChange={handleMonthChange}
                    className="bg-[#FAF7F2] border border-[rgba(61,46,30,0.1)] rounded-lg px-3 py-2 text-[#3D2E1E] text-sm focus:outline-none focus:border-[#C4935A]"
                  />
                ) : (
                  <input
                    type="date"
                    value={inputDateValue}
                    onChange={handleDateChange}
                    className="bg-[#FAF7F2] border border-[rgba(61,46,30,0.1)] rounded-lg px-3 py-2 text-[#3D2E1E] text-sm focus:outline-none focus:border-[#C4935A]"
                  />
                )}
                {!isCurrentPeriod && (
                  <button
                    onClick={resetToToday}
                    className="mt-2 w-full text-xs text-[#C4935A] hover:text-[#3D2E1E] transition-colors"
                  >
                    {viewMode === 'daily' ? 'Reset to Today' : viewMode === 'weekly' ? 'Reset to This Week' : 'Reset to This Month'}
                  </button>
                )}
              </div>
            )}
          </div>

          <button
            onClick={() => navigatePeriod(1)}
            className="p-1.5 text-[#2C2825]/45 hover:text-[#3D2E1E] hover:bg-[#F5EDE3] rounded transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <button className="bg-transparent border border-[rgba(61,46,30,0.12)] text-[#2C2825]/55 px-5 py-2.5 rounded-lg font-semibold text-sm hover:border-[rgba(61,46,30,0.25)] hover:text-[#3D2E1E] transition-all">
          Export Data
        </button>
        <button 
          onClick={onAddFamily}
          className="bg-[#C4935A] text-white px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-[#B8874E] hover:-translate-y-0.5 transition-all shadow-sm"
        >
          + Add Family
        </button>
        <button
          onClick={() => signOut(auth)}
          className="text-[#2C2825]/45 hover:text-[#3D2E1E] px-3 py-2.5 text-sm font-medium transition-colors"
        >
          Sign Out
        </button>
      </div>
    </div>
  );
};

// --- Stats Row Component ---

const StatsRow = ({ completedCalls, avgDuration, completionRate, failedCalls, activeFamilies, totalCalls, selectedDate, viewMode }) => {
  const today = new Date();
  const { startDate, endDate } = getDateRange(viewMode, selectedDate);
  
  const isCurrentPeriod = viewMode === 'daily' 
    ? selectedDate.toDateString() === today.toDateString()
    : viewMode === 'weekly'
      ? isInDateRange({ toDate: () => today }, startDate, endDate)
      : selectedDate.getMonth() === today.getMonth() && selectedDate.getFullYear() === today.getFullYear();

  let dateLabel, subLabel, durationSubLabel;
  if (viewMode === 'daily') {
    dateLabel = isCurrentPeriod ? "Today's Calls" : selectedDate.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " Calls";
    subLabel = isCurrentPeriod ? "completed today" : "completed on " + selectedDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    durationSubLabel = isCurrentPeriod ? "average today" : "average for day";
  } else if (viewMode === 'weekly') {
    dateLabel = isCurrentPeriod ? "This Week's Calls" : "Week's Calls";
    subLabel = isCurrentPeriod ? "completed this week" : `${startDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${endDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
    durationSubLabel = isCurrentPeriod ? "average this week" : "average for week";
  } else {
    dateLabel = isCurrentPeriod ? "This Month's Calls" : selectedDate.toLocaleDateString("en-US", { month: "short" }) + " Calls";
    subLabel = isCurrentPeriod ? "completed this month" : "completed in " + selectedDate.toLocaleDateString("en-US", { month: "long" });
    durationSubLabel = isCurrentPeriod ? "average this month" : "average for month";
  }

  return (
    <div className="grid grid-cols-4 gap-4 mb-8">
      <div className="bg-white border border-[rgba(61,46,30,0.08)] rounded-xl p-5 hover:border-[rgba(61,46,30,0.15)] hover:shadow-sm transition-all">
        <div className="text-[10px] uppercase tracking-[2px] text-[#C4935A] font-medium mb-2">{dateLabel}</div>
        <div className="font-['Lora'] text-[32px] font-medium text-[#3D2E1E] mb-1">{completedCalls}</div>
        <div className="text-sm text-[#2C2825]/45 font-light">{subLabel}</div>
      </div>

      <div className="bg-white border border-[rgba(61,46,30,0.08)] rounded-xl p-5 hover:border-[rgba(61,46,30,0.15)] hover:shadow-sm transition-all">
        <div className="text-[10px] uppercase tracking-[2px] text-[#C4935A] font-medium mb-2">Avg Call Duration</div>
        <div className="font-['Lora'] text-[32px] font-medium text-[#3D2E1E] mb-1">{formatDurationLong(avgDuration)}</div>
        <div className="text-sm text-[#2C2825]/45 font-light">{durationSubLabel}</div>
      </div>

      <div className="bg-white border border-[rgba(61,46,30,0.08)] rounded-xl p-5 hover:border-[rgba(61,46,30,0.15)] hover:shadow-sm transition-all">
        <div className="text-[10px] uppercase tracking-[2px] text-[#C4935A] font-medium mb-2">Completion Rate</div>
        <div className="font-['Lora'] text-[32px] font-medium text-[#3D2E1E] mb-1">{completionRate}%</div>
        <div className="text-sm font-light">
          {failedCalls > 0 ? (
            <span className="text-[#B87A5E]">{failedCalls} failed</span>
          ) : (
            <span className="text-[#8A9A7B]">no failures</span>
          )}
        </div>
      </div>

      <div className="bg-white border border-[rgba(61,46,30,0.08)] rounded-xl p-5 hover:border-[rgba(61,46,30,0.15)] hover:shadow-sm transition-all">
        <div className="text-[10px] uppercase tracking-[2px] text-[#C4935A] font-medium mb-2">Active Families</div>
        <div className="font-['Lora'] text-[32px] font-medium text-[#3D2E1E] mb-1">{activeFamilies}</div>
        <div className="text-sm text-[#2C2825]/45 font-light">{totalCalls} total calls</div>
      </div>
    </div>
  );
};

// --- Today's Calls Card ---

const TodaysCallsCard = ({ families, todaysCalls, selectedDate, viewMode }) => {
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const today = new Date();
  
  const { startDate, endDate } = getDateRange(viewMode, selectedDate);
  
  const selectedDay = dayNames[selectedDate.getDay()];
  const selectedDateStr = selectedDate.toISOString().split('T')[0];
  
  const isCurrentPeriod = viewMode === 'daily' 
    ? selectedDate.toDateString() === today.toDateString()
    : viewMode === 'weekly'
      ? isInDateRange({ toDate: () => today }, startDate, endDate)
      : selectedDate.getMonth() === today.getMonth() && selectedDate.getFullYear() === today.getFullYear();
  
  const getDaysInRange = () => {
    if (viewMode === 'daily') return [dayNames[selectedDate.getDay()]];
    const days = new Set();
    const current = new Date(startDate);
    while (current <= endDate) {
      days.add(dayNames[current.getDay()]);
      current.setDate(current.getDate() + 1);
    }
    return Array.from(days);
  };
  
  const daysInRange = getDaysInRange();
  
  const getDatesInRange = () => {
    const dates = [];
    const current = new Date(startDate);
    while (current <= endDate) {
      dates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };
  
  const datesInRange = getDatesInRange();

  const callHasTranscript = (call) => {
    return !!(
      call.rawPayload?.transcript ||
      call.rawPayload?.messages ||
      call.rawPayload?.conversation ||
      call.rawPayload?.data?.transcript ||
      call.rawPayload?.data?.messages ||
      call.transcript
    );
  };

  const findMatchingCall = (family) => {
    const normalizedFamilyPhone = normalizePhone(family.phone);
    return todaysCalls.find(call => {
      const callPhone = getCallPhoneNumber(call);
      return normalizePhone(callPhone) === normalizedFamilyPhone || call.familyId === family.id;
    });
  };

  const getCallStatusFromMatch = (matchingCall) => {
    if (!matchingCall) return { status: 'scheduled', duration: 0, callTime: null };
    
    const duration = matchingCall.rawPayload?.metadata?.call_duration_secs || 
                     matchingCall.rawPayload?.duration || 0;
    const callTime = matchingCall.receivedAt;
    
    let status = 'scheduled';
    if (callHasTranscript(matchingCall) || duration > 0) {
      status = 'completed';
    } else if (matchingCall.receivedAt) {
      status = 'no-answer';
    }
    
    return { status, duration, callTime };
  };

  const todaysEntries = useMemo(() => {
    const entries = [];
    
    families.forEach(family => {
      if (!family.callSchedule || !Array.isArray(family.callSchedule)) {
        if (viewMode === 'daily' ? family.callDay === selectedDay : daysInRange.includes(family.callDay)) {
          const matchingCall = findMatchingCall(family);
          const { status, duration, callTime } = getCallStatusFromMatch(matchingCall);
          
          entries.push({
            id: `old-${family.id}`,
            familyId: family.id,
            name: family.name || family.nickname || 'Unknown',
            phone: family.phone,
            scheduledTime: family.callTime || '',
            scheduledDay: family.callDay,
            status,
            duration,
            callTime,
            type: 'recurring',
          });
        }
        return;
      }

      family.callSchedule.forEach((schedule, index) => {
        let isScheduledInRange = false;
        
        if (schedule.type === 'recurring') {
          isScheduledInRange = viewMode === 'daily' 
            ? schedule.day === selectedDay
            : daysInRange.includes(schedule.day);
        } else if (schedule.type === 'one-time') {
          isScheduledInRange = datesInRange.includes(schedule.date) && !schedule.completed;
        } else {
          isScheduledInRange = viewMode === 'daily' 
            ? schedule.day === selectedDay
            : daysInRange.includes(schedule.day);
        }

        if (isScheduledInRange) {
          const matchingCall = findMatchingCall(family);
          const { status, duration, callTime } = getCallStatusFromMatch(matchingCall);
          
          entries.push({
            id: `${schedule.type || 'recurring'}-${family.id}-${index}`,
            familyId: family.id,
            name: family.name || family.nickname || 'Unknown',
            phone: family.phone,
            scheduledTime: schedule.time,
            scheduledDay: schedule.day || schedule.date,
            status,
            duration,
            callTime,
            type: schedule.type || 'recurring',
          });
        }
      });
    });
    
    return entries;
  }, [families, todaysCalls, viewMode, selectedDay, daysInRange, datesInRange]);

  const recurringEntries = todaysEntries.filter(e => e.type === 'recurring');
  const oneTimeEntries = todaysEntries.filter(e => e.type === 'one-time');

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-[#8A9A7B]/10 text-[#8A9A7B]';
      case 'no-answer': return 'bg-[#B87A5E]/10 text-[#B87A5E]';
      case 'failed': return 'bg-red-500/10 text-red-500';
      default: return 'bg-[#C4935A]/10 text-[#C4935A]';
    }
  };

  const renderCallEntry = (entry) => (
    <div 
      key={entry.id}
      className="flex items-center justify-between px-6 py-4 border-b border-[rgba(61,46,30,0.06)] last:border-0 hover:bg-[#F5EDE3]/50 transition-colors cursor-pointer"
    >
      <div className="flex items-center gap-3.5">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm ${getStatusColor(entry.status)}`}>
          {getInitials(entry.name)}
        </div>
        <div>
          <div className="font-medium text-[#3D2E1E] mb-0.5 flex items-center gap-2">
            {entry.name}
            {entry.type === 'one-time' && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#C4A49A]/15 text-[#C4A49A] uppercase font-semibold">One-time</span>
            )}
          </div>
          <div className="text-sm text-[#2C2825]/45 font-light">
            {entry.status === 'scheduled' 
              ? `Scheduled: ${entry.scheduledTime}` 
              : formatTime(entry.callTime)}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {entry.duration > 0 && (
          <span className="text-sm text-[#2C2825]/45 tabular-nums">{formatDuration(entry.duration)}</span>
        )}
        <StatusBadge status={entry.status} />
      </div>
    </div>
  );

  let headerTitle, emptyMessage;
  if (viewMode === 'daily') {
    headerTitle = isCurrentPeriod ? "Today's Calls" : `Calls for ${selectedDate.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}`;
    emptyMessage = isCurrentPeriod ? "No calls scheduled for today" : `No calls scheduled for ${selectedDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  } else if (viewMode === 'weekly') {
    headerTitle = isCurrentPeriod ? "This Week's Calls" : `Calls for ${startDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${endDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
    emptyMessage = isCurrentPeriod ? "No calls scheduled for this week" : `No calls scheduled for this week`;
  } else {
    headerTitle = isCurrentPeriod ? "This Month's Calls" : `Calls for ${selectedDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })}`;
    emptyMessage = isCurrentPeriod ? "No calls scheduled for this month" : `No calls scheduled for ${selectedDate.toLocaleDateString("en-US", { month: "long" })}`;
  }

  return (
    <div className="bg-white border border-[rgba(61,46,30,0.08)] rounded-xl overflow-hidden">
      <div className="flex justify-between items-center px-6 py-5 border-b border-[rgba(61,46,30,0.08)]">
        <span className="font-['Lora'] text-lg font-medium text-[#3D2E1E]">{headerTitle}</span>
        {isCurrentPeriod && (
          <span className="text-[10px] font-semibold uppercase tracking-[1.5px] px-2.5 py-1 rounded-full bg-[#8A9A7B]/10 text-[#8A9A7B] inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse-dot" />
            Live
          </span>
        )}
      </div>

      {todaysEntries.length === 0 ? (
        <div className="px-6 py-8 text-center text-[#2C2825]/45 font-light">{emptyMessage}</div>
      ) : (
        <>
          {recurringEntries.length > 0 && (
            <>
              <div className="px-6 py-2 bg-[#FAF7F2] text-[10px] uppercase tracking-[2px] text-[#2C2825]/35 font-medium flex items-center gap-2">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                </svg>
                Recurring
              </div>
              {recurringEntries.map(renderCallEntry)}
            </>
          )}

          {oneTimeEntries.length > 0 && (
            <>
              <div className="px-6 py-2 bg-[#FAF7F2] text-[10px] uppercase tracking-[2px] text-[#2C2825]/35 font-medium flex items-center gap-2 border-t border-[rgba(61,46,30,0.06)]">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                One-Time
              </div>
              {oneTimeEntries.map(renderCallEntry)}
            </>
          )}
        </>
      )}
    </div>
  );
};

// --- Family Roster Table ---

const FamilyRoster = ({ families, calls, onCallNow, onAddFamily, onEditFamily }) => {
  const [callingId, setCallingId] = useState(null);
  const [expandedFamilyId, setExpandedFamilyId] = useState(null);

  const getFamilyCalls = (family) => {
    const normalizedFamilyPhone = normalizePhone(family.phone);
    return calls.filter(c => {
      if (c.familyId === family.id) return true;
      const callPhone = getCallPhoneNumber(c);
      return normalizedFamilyPhone && normalizePhone(callPhone) === normalizedFamilyPhone;
    }).filter(c => {
      // Only show calls with actual duration/transcript
      const transcript = c.rawPayload?.data?.transcript;
      if (!Array.isArray(transcript) || transcript.length === 0) return false;
      const lastMessage = transcript[transcript.length - 1];
      const secs = lastMessage?.time_in_call_secs;
      return typeof secs === 'number' && secs > 0;
    }).sort((a, b) => {
      const aTime = a.receivedAt?.toDate?.() || new Date(0);
      const bTime = b.receivedAt?.toDate?.() || new Date(0);
      return bTime - aTime;
    });
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  };

  const handleCallNow = async (familyId) => {
    setCallingId(familyId);
    try {
      await onCallNow(familyId);
    } finally {
      setCallingId(null);
    }
  };

  const getFamilyStats = (family) => {
    const normalizedFamilyPhone = normalizePhone(family.phone);
    const familyCalls = calls.filter(c => {
      const callPhone = getCallPhoneNumber(c);
      return (normalizedFamilyPhone && normalizePhone(callPhone) === normalizedFamilyPhone) || c.familyId === family.id;
    });

    const lastCall = familyCalls[0];
    const totalCalls = familyCalls.length;
    
    let status = 'active';
    if (lastCall) {
      const callStatus = lastCall.rawPayload?.metadata?.call_status || lastCall.status;
      if (callStatus === 'no-answer' || callStatus === 'no_answer') status = 'missed';
    }

    return { lastCall, totalCalls, status };
  };

  const formatLastCall = (call) => {
    if (!call?.receivedAt?.toDate) return "No calls yet";
    const date = call.receivedAt.toDate();
    const today = new Date();
    
    if (date.toDateString() === today.toDateString()) {
      return `Today, ${date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
    }
    
    return date.toLocaleDateString("en-US", { 
      month: "short", 
      day: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  };

  return (
    <div className="bg-white border border-[rgba(61,46,30,0.08)] rounded-xl overflow-hidden">
      <div className="flex justify-between items-center px-6 py-5 border-b border-[rgba(61,46,30,0.08)]">
        <span className="font-['Lora'] text-lg font-medium text-[#3D2E1E]">Family Roster</span>
        <button 
          onClick={onAddFamily}
          className="bg-[#C4935A] text-white px-3.5 py-1.5 rounded-lg font-semibold text-xs hover:bg-[#B8874E] transition-all shadow-sm"
        >
          + Add Family
        </button>
      </div>

      <table className="w-full">
        <thead>
          <tr>
            <th className="text-left px-6 py-3 text-[10px] uppercase tracking-[2px] text-[#2C2825]/35 font-medium border-b border-[rgba(61,46,30,0.06)]">Name</th>
            <th className="text-left px-6 py-3 text-[10px] uppercase tracking-[2px] text-[#2C2825]/35 font-medium border-b border-[rgba(61,46,30,0.06)]">Family Member</th>
            <th className="text-left px-6 py-3 text-[10px] uppercase tracking-[2px] text-[#2C2825]/35 font-medium border-b border-[rgba(61,46,30,0.06)]">Plan</th>
            <th className="text-left px-6 py-3 text-[10px] uppercase tracking-[2px] text-[#2C2825]/35 font-medium border-b border-[rgba(61,46,30,0.06)]">Phone</th>
            <th className="text-left px-6 py-3 text-[10px] uppercase tracking-[2px] text-[#2C2825]/35 font-medium border-b border-[rgba(61,46,30,0.06)]">Schedule</th>
            <th className="text-left px-6 py-3 text-[10px] uppercase tracking-[2px] text-[#2C2825]/35 font-medium border-b border-[rgba(61,46,30,0.06)]">Last Call</th>
            <th className="text-left px-6 py-3 text-[10px] uppercase tracking-[2px] text-[#2C2825]/35 font-medium border-b border-[rgba(61,46,30,0.06)]">Total Calls</th>
            <th className="text-left px-6 py-3 text-[10px] uppercase tracking-[2px] text-[#2C2825]/35 font-medium border-b border-[rgba(61,46,30,0.06)]">Status</th>
            <th className="text-left px-6 py-3 text-[10px] uppercase tracking-[2px] text-[#2C2825]/35 font-medium border-b border-[rgba(61,46,30,0.06)]"></th>
          </tr>
        </thead>
        <tbody>
          {families.map((family) => {
            const { lastCall, status } = getFamilyStats(family);
            
            let recurringDisplay = '';
            let oneTimeCount = 0;
            
            if (family.callSchedule && Array.isArray(family.callSchedule) && family.callSchedule.length > 0) {
              const recurring = family.callSchedule.filter(s => s.type === 'recurring' || !s.type);
              const oneTime = family.callSchedule.filter(s => s.type === 'one-time' && !s.completed);
              
              if (recurring.length > 0) {
                recurringDisplay = recurring.map(s => {
                  let displayTime = s.time;
                  if (/^\d{2}:\d{2}$/.test(s.time)) {
                    let [hour, min] = s.time.split(':').map(Number);
                    const ampm = hour >= 12 ? 'PM' : 'AM';
                    hour = hour % 12 || 12;
                    displayTime = `${hour}:${min.toString().padStart(2, '0')} ${ampm}`;
                  }
                  return `${s.day?.slice(0, 3)} @ ${displayTime}`;
                }).join(', ');
              }
              oneTimeCount = oneTime.length;
            }
            else if (family.callDay && family.callTime) {
              recurringDisplay = `${family.callDay} @ ${family.callTime}`;
            }
            
            const schedule = recurringDisplay || (oneTimeCount > 0 ? `${oneTimeCount} one-time call${oneTimeCount > 1 ? 's' : ''}` : "Not scheduled");

            return (
              <React.Fragment key={family.id}>
              <tr
                key={family.id}
                className="hover:bg-[#F5EDE3]/40 transition-colors cursor-pointer"
                onClick={() => setExpandedFamilyId(expandedFamilyId === family.id ? null : family.id)}
              >
                <td className="px-6 py-4 font-medium text-[#3D2E1E] border-b border-[rgba(61,46,30,0.06)]">
                  {family.name || family.nickname || "Unknown"}
                </td>
                <td className="px-6 py-4 text-[#2C2825]/55 border-b border-[rgba(61,46,30,0.06)]">
                  {family.adultChildName || "—"}
                </td>
                <td className="px-6 py-4 text-[#2C2825]/55 border-b border-[rgba(61,46,30,0.06)]">
                  {family.planType === 'legacy' ? 'Legacy' : family.planType === 'test' ? 'Test' : family.planType === 'inactive' ? 'Inactive' : 'Essential'}
                </td>
                <td className="px-6 py-4 text-[#2C2825]/55 tabular-nums border-b border-[rgba(61,46,30,0.06)]">
                  {family.phone || "—"}
                </td>
                <td className="px-6 py-4 text-[#2C2825]/55 border-b border-[rgba(61,46,30,0.06)]">
                  <div className="space-y-1">
                    <div>{schedule}</div>
                    {oneTimeCount > 0 && recurringDisplay && (
                      <div className="text-xs text-[#C4A49A]">+{oneTimeCount} one-time</div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-[#2C2825]/55 border-b border-[rgba(61,46,30,0.06)]">
                  {formatLastCall(lastCall)}
                </td>
                <td className="px-6 py-4 text-[#3D2E1E] tabular-nums border-b border-[rgba(61,46,30,0.06)]">
                  {family.callCount || 0}
                </td>
                <td className="px-6 py-4 border-b border-[rgba(61,46,30,0.06)]">
                  <StatusBadge status={status} />
                </td>
                <td className="px-6 py-4 border-b border-[rgba(61,46,30,0.06)]">
                  <div className="flex gap-2">
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleCallNow(family.id); }}
                      disabled={callingId === family.id}
                      className="border border-[#C4935A] text-[#C4935A] px-3 py-1.5 rounded-md text-xs font-medium hover:bg-[#C4935A] hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {callingId === family.id ? "Calling..." : "Call Now"}
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); onEditFamily(family); }}
                      className="border border-[rgba(61,46,30,0.12)] text-[#2C2825]/55 px-3 py-1.5 rounded-md text-xs font-medium hover:border-[#C4935A] hover:text-[#C4935A] transition-all"
                    >
                      Edit
                    </button>
                  </div>
                </td>
              </tr>
              {expandedFamilyId === family.id && (
                <tr key={`${family.id}-recordings`}>
                  <td colSpan="9" className="p-0">
                    <div className="bg-[#FAF7F2] border-t border-[rgba(61,46,30,0.06)] px-6 py-4">
                      <div className="text-[10px] uppercase tracking-[2px] text-[#C4935A] font-medium mb-3">Call Recordings</div>
                      {(() => {
                        const familyCalls = getFamilyCalls(family);
                        if (familyCalls.length === 0) {
                          return <div className="text-sm text-[#2C2825]/45 font-light py-2">No recordings yet</div>;
                        }
                        return (
                          <div className="space-y-2">
                            {familyCalls.map((call) => {
                              const callDate = call.receivedAt?.toDate?.();
                              const dateStr = callDate ? callDate.toLocaleDateString("en-US", {
                                weekday: "short", month: "short", day: "numeric", year: "numeric"
                              }) : "Unknown date";
                              const timeStr = callDate ? callDate.toLocaleTimeString("en-US", {
                                hour: "numeric", minute: "2-digit", hour12: true
                              }) : "";
                              const transcript = call.rawPayload?.data?.transcript;
                              const duration = Array.isArray(transcript) && transcript.length > 0
                                ? transcript[transcript.length - 1]?.time_in_call_secs || 0
                                : 0;
                              const durationStr = duration > 0
                                ? `${Math.floor(duration / 60)}:${(duration % 60).toString().padStart(2, '0')}`
                                : "";
                              const recordingLink = call.recordingUrl || "";

                              return (
                                <div
                                  key={call.id}
                                  className="flex items-center justify-between bg-white rounded-lg border border-[rgba(61,46,30,0.08)] px-4 py-3"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="flex items-center gap-4">
                                    <div>
                                      <div className="text-sm font-medium text-[#3D2E1E]">{dateStr}</div>
                                      <div className="text-xs text-[#2C2825]/45">{timeStr}{durationStr ? ` · ${durationStr}` : ""}</div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {recordingLink && (
                                      <>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            copyToClipboard(recordingLink);
                                          }}
                                          className="border border-[rgba(61,46,30,0.12)] text-[#2C2825]/55 px-3 py-1.5 rounded-md text-xs font-medium hover:border-[#C4935A] hover:text-[#C4935A] transition-all"
                                        >
                                          Copy Link
                                        </button>
                                        <a
                                          href={recordingLink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          className="border border-[#C4935A] text-[#C4935A] px-3 py-1.5 rounded-md text-xs font-medium hover:bg-[#C4935A] hover:text-white transition-all"
                                        >
                                          Listen
                                        </a>
                                      </>
                                    )}
                                    {!recordingLink && (
                                      <span className="text-xs text-[#2C2825]/35">No recording</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {families.length === 0 && (
        <div className="px-6 py-8 text-center text-[#2C2825]/45 font-light">No families added yet</div>
      )}
    </div>
  );
};

// --- Transcript Display Component ---

const TranscriptDisplay = ({ call, onCollapse }) => {
  const getTranscriptData = () => {
    const possibleLocations = [
      call.rawPayload?.transcript,
      call.rawPayload?.messages,
      call.rawPayload?.conversation,
      call.rawPayload?.data?.transcript,
      call.rawPayload?.data?.messages,
      call.rawPayload?.data?.conversation,
      call.transcript,
    ];

    for (const data of possibleLocations) {
      if (data) return data;
    }
    return null;
  };

  const transcriptData = getTranscriptData();

  const parseStringTranscript = (text) => {
    if (!text || typeof text !== 'string') return [];
    const lines = text.split('\n').filter(line => line.trim());
    return lines.map((line, index) => {
      const colonIndex = line.indexOf(':');
      if (colonIndex > 0 && colonIndex < 20) {
        const role = line.substring(0, colonIndex).trim().toLowerCase();
        const content = line.substring(colonIndex + 1).trim();
        const isAgent = role.includes('emily') || role.includes('remi') || role.includes('agent') || role.includes('assistant') || role.includes('ai');
        return { role: isAgent ? 'agent' : 'user', content, id: index };
      }
      return { role: 'unknown', content: line.trim(), id: index };
    });
  };

  const normalizeMessages = (messages) => {
    if (!Array.isArray(messages)) return [];
    return messages.map((msg, index) => {
      const role = (msg.role || msg.speaker || msg.source || 'unknown').toLowerCase();
      const isAgent = role.includes('agent') || role.includes('assistant') || role.includes('ai') || role === 'emily' || role === 'remi';
      return {
        role: isAgent ? 'agent' : 'user',
        content: msg.content || msg.text || msg.message || (typeof msg === 'string' ? msg : ''),
        id: index,
      };
    }).filter(msg => msg.content);
  };

  let messages = [];
  if (transcriptData) {
    if (Array.isArray(transcriptData)) {
      messages = normalizeMessages(transcriptData);
    } else if (typeof transcriptData === 'string') {
      messages = parseStringTranscript(transcriptData);
    } else if (typeof transcriptData === 'object') {
      const innerMessages = transcriptData.messages || transcriptData.conversation || transcriptData.data;
      if (Array.isArray(innerMessages)) {
        messages = normalizeMessages(innerMessages);
      }
    }
  }

  return (
    <div className="bg-[#FAF7F2] border-t border-[rgba(61,46,30,0.08)]">
      {/* Header */}
      <div className="flex justify-between items-center px-6 py-3 border-b border-[rgba(61,46,30,0.06)]">
        <span className="text-[10px] uppercase tracking-[2px] text-[#C4935A] font-medium">Conversation Transcript</span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onCollapse();
          }}
          className="text-xs text-[#C4935A] hover:text-[#3D2E1E] transition-colors flex items-center gap-1"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="18 15 12 9 6 15"></polyline>
          </svg>
          Collapse
        </button>
      </div>

      {/* Transcript content */}
      <div className="px-6 py-4 max-h-80 overflow-y-auto space-y-3">
        {messages.length === 0 ? (
          <div className="text-center text-[#2C2825]/45 py-4 font-light">No transcript available</div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.role === 'agent' ? 'justify-start' : 'justify-end'}`}
            >
              <div
                className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm ${
                  msg.role === 'agent'
                    ? 'bg-[#C4935A]/10 text-[#3D2E1E] rounded-bl-sm'
                    : 'bg-white text-[#3D2E1E] rounded-br-sm border border-[rgba(61,46,30,0.08)] shadow-sm'
                }`}
              >
                <div className={`text-[10px] uppercase tracking-[1.5px] mb-1 font-medium ${
                  msg.role === 'agent' ? 'text-[#C4935A]' : 'text-[#2C2825]/35'
                }`}>
                  {msg.role === 'agent' ? 'Remi' : 'Senior'}
                </div>
                <div className="leading-relaxed font-light">{msg.content}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// --- Recent Call Log ---

const RecentCallLog = ({ calls, families }) => {
  const [expandedCall, setExpandedCall] = useState(null);

  const callHasTranscript = (call) => {
    return !!(
      call.rawPayload?.transcript ||
      call.rawPayload?.messages ||
      call.rawPayload?.conversation ||
      call.rawPayload?.data?.transcript ||
      call.rawPayload?.data?.messages ||
      call.transcript
    );
  };

  const filteredCalls = useMemo(() => {
    const completedCalls = calls.filter(call => {
      const duration = call.rawPayload?.metadata?.call_duration_secs || 
                       call.rawPayload?.duration || 0;
      const hasTranscript = callHasTranscript(call);
      return hasTranscript || duration > 0;
    });

    const seen = new Set();
    const deduplicated = completedCalls.filter(call => {
      const conversationId = call.rawPayload?.data?.conversation_id || 
                             call.rawPayload?.conversation_id ||
                             call.id;
      
      if (seen.has(conversationId)) {
        return false;
      }
      seen.add(conversationId);
      return true;
    });

    return deduplicated;
  }, [calls]);

  const getFamilyName = (call) => {
    const callPhone = getCallPhoneNumber(call);
    const normalizedCallPhone = normalizePhone(callPhone);
    
    if (families[callPhone]) {
      return families[callPhone].name || families[callPhone].nickname || "Unknown";
    }
    
    if (call.familyId && families[call.familyId]) {
      return families[call.familyId].name || families[call.familyId].nickname || "Unknown";
    }

    if (normalizedCallPhone) {
      for (const key in families) {
        const family = families[key];
        if (family.phone && normalizePhone(family.phone) === normalizedCallPhone) {
          return family.name || family.nickname || "Unknown";
        }
      }
    }

    return "Unknown";
  };

  const formatCallTime = (timestamp) => {
    if (!timestamp?.toDate) return "Unknown";
    const date = timestamp.toDate();
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    }
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const toggleExpand = (e, callId) => {
    e.stopPropagation();
    setExpandedCall(expandedCall === callId ? null : callId);
  };

  return (
    <div className="bg-white border border-[rgba(61,46,30,0.08)] rounded-xl overflow-hidden">
      <div className="flex justify-between items-center px-6 py-5 border-b border-[rgba(61,46,30,0.08)]">
        <span className="font-['Lora'] text-lg font-medium text-[#3D2E1E]">Recent Call Log</span>
        <button className="bg-transparent border border-[rgba(61,46,30,0.12)] text-[#2C2825]/55 px-3.5 py-1.5 rounded-lg font-semibold text-xs hover:border-[rgba(61,46,30,0.25)] hover:text-[#3D2E1E] transition-all">
          View All
        </button>
      </div>

      {filteredCalls.slice(0, 10).map((call, index) => {
        const duration = call.rawPayload?.metadata?.call_duration_secs || call.rawPayload?.duration || 0;
        const isExpanded = expandedCall === call.id;
        const transcriptAvailable = callHasTranscript(call);

        return (
          <div key={call.id || index} className="border-b border-[rgba(61,46,30,0.06)] last:border-0">
            <div 
              className={`grid grid-cols-[70px_1fr_56px_88px_200px] items-center gap-4 px-6 py-3.5 hover:bg-[#F5EDE3]/40 transition-colors cursor-pointer group ${isExpanded ? 'bg-[#F5EDE3]/50' : ''}`}
            >
              <span className="text-xs text-[#2C2825]/45 tabular-nums">
                {formatCallTime(call.receivedAt)}
              </span>
              <span className="font-medium text-[#3D2E1E] min-w-0 truncate">{getFamilyName(call)}</span>
              <span className="text-sm text-[#2C2825]/45 tabular-nums">
                {duration > 0 ? formatDuration(duration) : ''}
              </span>
              <div className="text-right">
                <StatusBadge status="completed" />
              </div>
              <div className="flex items-center gap-2 min-w-0">
                <button
                  onClick={(e) => toggleExpand(e, call.id)}
                  className={`text-xs px-2 py-1 rounded transition-all flex-shrink-0 ${
                    isExpanded 
                      ? 'text-[#C4935A] bg-[#C4935A]/8' 
                      : transcriptAvailable
                        ? 'text-[#C4935A] opacity-0 group-hover:opacity-100'
                        : 'text-[#2C2825]/30 opacity-0 group-hover:opacity-100'
                  }`}
                >
                  {isExpanded ? 'Hide' : 'View Transcript'}
                </button>
                {call.rawPayload?.data?.conversation_id ? (
                  <a
                    href={`https://elevenlabs.io/app/conversational-ai/history/${call.rawPayload.data.conversation_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs px-2 py-1 rounded transition-all text-[#8A9A7B] opacity-0 group-hover:opacity-100 hover:bg-[#8A9A7B]/10 flex-shrink-0"
                  >
                    Listen
                  </a>
                ) : (
                  <span className="w-[52px] flex-shrink-0" aria-hidden="true" />
                )}
              </div>
            </div>

            {isExpanded && (
              <TranscriptDisplay 
                call={call} 
                onCollapse={() => setExpandedCall(null)} 
              />
            )}
          </div>
        );
      })}

      {filteredCalls.length === 0 && (
        <div className="px-6 py-8 text-center text-[#2C2825]/45 font-light">No completed calls yet</div>
      )}

      <div className="text-xs text-[#2C2825]/35 px-6 py-3 text-center border-t border-[rgba(61,46,30,0.06)]">
        Click "View Transcript" to expand conversation
      </div>
    </div>
  );
};

// --- Login Screen ---

const LoginScreen = () => {
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState(null);

  const handleGoogleSignIn = async () => {
    setError(null);
    setSigningIn(true);
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (err) {
      console.error("Sign-in error:", err);
      setError(err.message || "Sign-in failed");
    } finally {
      setSigningIn(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAF7F2] text-[#3D2E1E] p-6">
      <div className="text-center max-w-sm w-full">
        <h1 className="font-['Lora'] text-4xl font-medium tracking-tight mb-2">
          <span className="text-[#C4935A]">Sonny</span> Admin
        </h1>
        <p className="text-[#2C2825]/45 text-sm font-light mb-8">Sign in to access the dashboard</p>
        <button
          onClick={handleGoogleSignIn}
          disabled={signingIn}
          className="w-full flex items-center justify-center gap-3 bg-white text-[#3D2E1E] px-5 py-3 rounded-lg font-medium text-sm hover:bg-[#F5EDE3] transition-all disabled:opacity-50 disabled:cursor-not-allowed border border-[rgba(61,46,30,0.1)] shadow-sm"
        >
          {signingIn ? (
            "Signing in..."
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </>
          )}
        </button>
        {error && (
          <p className="mt-4 text-red-500 text-sm">{error}</p>
        )}
      </div>
    </div>
  );
};

// --- Main App ---

const CostTracker = ({ calls, families, selectedDate, viewMode }) => {
  const { startDate, endDate } = getDateRange(viewMode, selectedDate);

  const periodData = useMemo(() => {
    const periodCalls = calls.filter(c => {
      if (!isInDateRange(c.receivedAt, startDate, endDate)) return false;
      const transcript = c.rawPayload?.data?.transcript;
      if (!Array.isArray(transcript) || transcript.length === 0) return false;
      const lastMessage = transcript[transcript.length - 1];
      const secs = lastMessage?.time_in_call_secs;
      return typeof secs === 'number' && secs > 0;
    });

    let totalSeconds = 0;
    periodCalls.forEach(call => {
      const transcript = call.rawPayload?.data?.transcript;
      if (Array.isArray(transcript) && transcript.length > 0) {
        const lastMessage = transcript[transcript.length - 1];
        const secs = lastMessage?.time_in_call_secs;
        if (typeof secs === 'number' && secs > 0) {
          totalSeconds += secs;
        }
      }
    });

    const totalMinutes = totalSeconds / 60;
    const totalCalls = periodCalls.length;

    let daysInPeriod = 1;
    const diffTime = Math.abs(endDate - startDate);
    daysInPeriod = Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1);
    const daysInMonth = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0).getDate();
    const periodFraction = Math.min(1, daysInPeriod / daysInMonth);

    const elevenLabsBase = COST_RATES.ELEVENLABS_BASE_MONTHLY * periodFraction;
    const includedMinutes = COST_RATES.ELEVENLABS_INCLUDED_MINUTES * periodFraction;
    const overageMinutes = Math.max(0, totalMinutes - includedMinutes);
    const elevenLabsOverage = overageMinutes * COST_RATES.ELEVENLABS_OVERAGE_PER_MIN;
    const elevenLabsTotal = elevenLabsBase + elevenLabsOverage;

    const twilioCost = totalMinutes * COST_RATES.TWILIO_PER_MIN;
    const claudeCost = totalCalls * COST_RATES.CLAUDE_PER_CALL;

    const totalCost = elevenLabsTotal + twilioCost + claudeCost;

    let totalRevenue = 0;
    let payingCustomers = 0;
    families.forEach(family => {
      const planPrice = COST_RATES.PLAN_PRICES[family.planType];
      if (planPrice === undefined) return;
      if (planPrice > 0) {
        totalRevenue += planPrice;
        payingCustomers++;
      }
    });

    const profit = totalRevenue - totalCost;
    const marginPercent = totalRevenue > 0 ? Math.round((profit / totalRevenue) * 100) : 0;

    const avgCallMinutes = totalCalls > 0 ? (totalMinutes / totalCalls) : 0;
    const avgCallCost = totalCalls > 0 ? (totalCost / totalCalls) : 0;

    return {
      totalCalls,
      totalMinutes,
      avgCallMinutes,
      elevenLabsTotal,
      twilioCost,
      claudeCost,
      totalCost,
      totalRevenue,
      profit,
      marginPercent,
      avgCallCost,
      overageMinutes,
      payingCustomers,
    };
  }, [calls, families, startDate, endDate, viewMode]);

  const fmt = (num) => {
    if (num >= 1) return `$${num.toFixed(2)}`;
    if (num > 0) return `$${num.toFixed(3)}`;
    return '$0.00';
  };

  const isProfit = periodData.profit >= 0;

  return (
    <div className="bg-white border border-[rgba(61,46,30,0.08)] rounded-xl overflow-hidden">
      <div className="flex justify-between items-center px-6 py-5 border-b border-[rgba(61,46,30,0.08)]">
        <span className="font-['Lora'] text-lg font-medium text-[#3D2E1E]">Cost Tracker</span>
        <span className={`text-sm font-semibold px-3 py-1 rounded-full ${
          isProfit ? 'bg-[#8A9A7B]/10 text-[#8A9A7B]' : 'bg-red-500/10 text-red-500'
        }`}>
          {isProfit ? '+' : ''}{fmt(periodData.profit)} ({periodData.marginPercent}% margin)
        </span>
      </div>

      <div className="grid grid-cols-3 gap-0 border-b border-[rgba(61,46,30,0.08)]">
        <div className="p-5 border-r border-[rgba(61,46,30,0.08)]">
          <div className="text-[10px] uppercase tracking-[2px] text-[#8A9A7B] font-medium mb-2">Revenue</div>
          <div className="font-['Lora'] text-2xl font-medium text-[#3D2E1E]">{fmt(periodData.totalRevenue)}</div>
          <div className="text-xs text-[#2C2825]/45 mt-1">{periodData.payingCustomers} paying customer{periodData.payingCustomers !== 1 ? 's' : ''}</div>
        </div>

        <div className="p-5 border-r border-[rgba(61,46,30,0.08)]">
          <div className="text-[10px] uppercase tracking-[2px] text-[#B87A5E] font-medium mb-2">Total Cost</div>
          <div className="font-['Lora'] text-2xl font-medium text-[#3D2E1E]">{fmt(periodData.totalCost)}</div>
          <div className="text-xs text-[#2C2825]/45 mt-1">{periodData.totalCalls} calls, {periodData.totalMinutes.toFixed(1)} min</div>
        </div>

        <div className="p-5">
          <div className="text-[10px] uppercase tracking-[2px] text-[#C4935A] font-medium mb-2">Profit</div>
          <div className={`font-['Lora'] text-2xl font-medium ${isProfit ? 'text-[#8A9A7B]' : 'text-red-500'}`}>
            {isProfit ? '+' : ''}{fmt(periodData.profit)}
          </div>
          <div className="text-xs text-[#2C2825]/45 mt-1">{periodData.marginPercent}% margin</div>
        </div>
      </div>

      <div className="px-6 py-4">
        <div className="text-[10px] uppercase tracking-[2px] text-[#2C2825]/35 font-medium mb-3">Cost Breakdown</div>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-[#2C2825]/55">ElevenLabs (base + overage)</span>
            <span className="text-sm font-medium text-[#3D2E1E] tabular-nums">{fmt(periodData.elevenLabsTotal)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-[#2C2825]/55">Twilio</span>
            <span className="text-sm font-medium text-[#3D2E1E] tabular-nums">{fmt(periodData.twilioCost)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-[#2C2825]/55">Claude API (summaries)</span>
            <span className="text-sm font-medium text-[#3D2E1E] tabular-nums">{fmt(periodData.claudeCost)}</span>
          </div>
          <div className="flex justify-between items-center pt-2 border-t border-[rgba(61,46,30,0.06)]">
            <span className="text-sm text-[#2C2825]/55">Avg cost per call</span>
            <span className="text-sm font-medium text-[#3D2E1E] tabular-nums">{fmt(periodData.avgCallCost)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-[#2C2825]/55">Avg call duration</span>
            <span className="text-sm font-medium text-[#3D2E1E] tabular-nums">{periodData.avgCallMinutes.toFixed(1)} min</span>
          </div>
          {periodData.overageMinutes > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-[#B87A5E]">ElevenLabs overage minutes</span>
              <span className="text-sm font-medium text-[#B87A5E] tabular-nums">{periodData.overageMinutes.toFixed(1)} min</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const App = () => {
  const [authUser, setAuthUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  const [calls, setCalls] = useState([]);
  const [familiesMap, setFamiliesMap] = useState({});
  const [familiesList, setFamiliesList] = useState([]);
  const [callError, setCallError] = useState(null);
  const [callSuccess, setCallSuccess] = useState(null);
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  const [editingFamily, setEditingFamily] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('daily');

  const { startDate: rangeStartDate, endDate: rangeEndDate } = useMemo(() => 
    getDateRange(viewMode, selectedDate),
    [viewMode, selectedDate]
  );

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setAuthUser(user);
      if (!user) {
        setIsAdmin(false);
        setAccessDenied(false);
        setAuthChecking(false);
        return;
      }
      try {
        const adminDoc = await getDoc(doc(db, "adminUsers", user.email));
        if (adminDoc.exists()) {
          setIsAdmin(true);
          setAccessDenied(false);
        } else {
          setAccessDenied(true);
          setIsAdmin(false);
          await signOut(auth);
        }
      } catch (err) {
        console.error("Admin check error:", err);
        setAccessDenied(true);
        setIsAdmin(false);
        await signOut(auth);
      } finally {
        setAuthChecking(false);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    const familiesUnsub = onSnapshot(collection(db, "families"), (snapshot) => {
      const familiesData = {};
      const familiesArray = [];
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        familiesData[data.phone] = { id: doc.id, ...data };
        familiesData[doc.id] = { id: doc.id, ...data };
        familiesArray.push({ id: doc.id, ...data });
      });
      setFamiliesMap(familiesData);
      setFamiliesList(familiesArray);
    });

    const q = query(collection(db, "calls"), orderBy("receivedAt", "desc"));
    const callsUnsub = onSnapshot(q, (snapshot) => {
      const callsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCalls(callsData);
    });

    return () => {
      familiesUnsub();
      callsUnsub();
    };
  }, [isAdmin]);

  const handleCallNow = async (familyId) => {
    setCallError(null);
    setCallSuccess(null);
    
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch('https://callfamily-mo2wxwjlxa-uc.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ familyId: familyId }),
      });

      if (!response.ok) {
        throw new Error(`Call failed: ${response.status}`);
      }

      const result = await response.json();
      setCallSuccess(`Call initiated successfully!`);
      setTimeout(() => setCallSuccess(null), 3000);
      return result;
    } catch (error) {
      console.error('Call Now error:', error);
      setCallError(error.message);
      setTimeout(() => setCallError(null), 5000);
      throw error;
    }
  };

  const getCallDuration = (call) => {
    const transcript = call.rawPayload?.data?.transcript;
    if (Array.isArray(transcript) && transcript.length > 0) {
      const lastMessage = transcript[transcript.length - 1];
      const secs = lastMessage?.time_in_call_secs;
      if (typeof secs === 'number' && secs > 0) return secs;
    }
    return 0;
  };

  const selectedRangeCalls = useMemo(() => 
    calls.filter(c => isInDateRange(c.receivedAt, rangeStartDate, rangeEndDate)), 
    [calls, rangeStartDate, rangeEndDate]
  );

  const completedRangeCalls = selectedRangeCalls.filter(c => 
    getCallDuration(c) > 0
  ).length;

  const rangeTranscriptionCalls = useMemo(() => 
    calls.filter(c => 
      c.rawPayload?.type === 'post_call_transcription' && 
      isInDateRange(c.receivedAt, rangeStartDate, rangeEndDate)
    ),
    [calls, rangeStartDate, rangeEndDate]
  );

  const avgDuration = useMemo(() => {
    const callsWithDuration = rangeTranscriptionCalls.filter(c => getCallDuration(c) > 0);
    if (callsWithDuration.length === 0) return 0;
    const total = callsWithDuration.reduce((sum, c) => sum + getCallDuration(c), 0);
    return Math.round(total / callsWithDuration.length);
  }, [rangeTranscriptionCalls]);

  const completionRate = useMemo(() => {
    if (rangeTranscriptionCalls.length === 0) return 0;
    const completed = rangeTranscriptionCalls.filter(c =>
      c.rawPayload?.data?.analysis?.call_successful !== "failure"
      && getCallDuration(c) > 0
    ).length;
    return Math.round((completed / rangeTranscriptionCalls.length) * 100);
  }, [rangeTranscriptionCalls]);

  const failedCalls = useMemo(() => {
    return rangeTranscriptionCalls.filter(c =>
      c.rawPayload?.data?.analysis?.call_successful === "failure"
    ).length;
  }, [rangeTranscriptionCalls]);

  const handleSaveFamily = async (familyId, familyData) => {
    console.log('[handleSaveFamily] Called with:', { familyId, familyData });
    console.log('[handleSaveFamily] callSchedule:', familyData?.callSchedule);
    try {
      const dataToWrite = {
        ...familyData,
        normalizedPhone: normalizePhone(familyData.phone || ''),
        ...(familyId ? { updatedAt: new Date() } : {}),
      };
      if (familyId) {
        const familyRef = doc(db, "families", familyId);
        await updateDoc(familyRef, dataToWrite);
        setCallSuccess("Family updated successfully!");
      } else {
        await addDoc(collection(db, "families"), dataToWrite);
        setCallSuccess("Family added successfully!");
      }
      setTimeout(() => setCallSuccess(null), 3000);
    } catch (error) {
      console.error("Error saving family:", error);
      setCallError("Failed to save family: " + error.message);
      setTimeout(() => setCallError(null), 5000);
      throw error;
    }
  };

  const handleDeleteFamily = async (familyId) => {
    try {
      const familyRef = doc(db, "families", familyId);
      await deleteDoc(familyRef);
      setCallSuccess("Family removed successfully!");
      setTimeout(() => setCallSuccess(null), 3000);
    } catch (error) {
      console.error("Error deleting family:", error);
      setCallError("Failed to delete family: " + error.message);
      setTimeout(() => setCallError(null), 5000);
      throw error;
    }
  };

  const handleAddFamily = () => {
    setEditingFamily(null);
    setShowFamilyModal(true);
  };

  const handleEditFamily = (family) => {
    setEditingFamily(family);
    setShowFamilyModal(true);
  };

  const handleCloseModal = () => {
    setShowFamilyModal(false);
    setEditingFamily(null);
  };

  if (authChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] text-[#3D2E1E]">
        <p className="text-[#2C2825]/45 font-light">Loading...</p>
      </div>
    );
  }

  if (!authUser) {
    return <LoginScreen />;
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAF7F2] text-[#3D2E1E] p-6">
        <h1 className="font-['Lora'] text-2xl font-medium text-[#C4935A] mb-2">Access Denied</h1>
        <p className="text-[#2C2825]/45 text-sm font-light mb-4">Your account is not authorized to access this dashboard.</p>
        <button
          onClick={() => signOut(auth)}
          className="text-sm text-[#2C2825]/45 hover:text-[#3D2E1E] underline transition-colors"
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8 bg-[#FAF7F2] text-[#3D2E1E]">
      {/* Family Modal */}
      <FamilyModal 
        isOpen={showFamilyModal}
        onClose={handleCloseModal}
        onSave={handleSaveFamily}
        onDelete={handleDeleteFamily}
        editFamily={editingFamily}
      />

      {/* Notifications */}
      {callError && (
        <div className="fixed top-4 right-4 bg-red-500 text-white px-4 py-3 rounded-lg shadow-lg z-50 animate-fade-in">
          {callError}
        </div>
      )}
      {callSuccess && (
        <div className="fixed top-4 right-4 bg-[#8A9A7B] text-white px-4 py-3 rounded-lg shadow-lg z-50 animate-fade-in">
          {callSuccess}
        </div>
      )}

      <Header 
        onAddFamily={handleAddFamily} 
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      <StatsRow 
        completedCalls={completedRangeCalls}
        avgDuration={avgDuration}
        completionRate={completionRate}
        failedCalls={failedCalls}
        activeFamilies={familiesList.length}
        totalCalls={calls.length}
        selectedDate={selectedDate}
        viewMode={viewMode}
      />

      <div className="mb-8">
        <TodaysCallsCard families={familiesList} todaysCalls={selectedRangeCalls} selectedDate={selectedDate} viewMode={viewMode} />
      </div>

      <div className="mb-8">
        <FamilyRoster families={familiesList} calls={calls} onCallNow={handleCallNow} onAddFamily={handleAddFamily} onEditFamily={handleEditFamily} />
      </div>

      <div className="mb-8">
        <CostTracker 
          calls={calls} 
          families={familiesList} 
          selectedDate={selectedDate} 
          viewMode={viewMode} 
        />
      </div>

      <div className="mb-8">
        <RecentCallLog calls={calls} families={familiesMap} />
      </div>

      <style>{`
        @keyframes fadeIn { 
          from { opacity: 0; transform: translateY(-10px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
      `}</style>
    </div>
  );
};

export default App;